import { getRouteApi } from '@tanstack/react-router';
import { DataTable, DataTableColumn } from 'cpool-components/general/DataTable';
import type { CustomSortFunction } from 'cpool-components/general/DataTable';
import type { SchemaLoanRequestResolver_GetAcceptedOffers } from 'cpool-tables/backend-resolvers';
import { useTranslation } from 'react-i18next';

import { OfferManagementTableRow } from './OfferManagementTableRow';
import { getOfferFinancingPurpose } from './utils/getOfferFinancingPurpose';
import { sortStatus } from './utils/sortStatus';
import { ReactComponent as NoBank } from '~/assets/no-bank.svg';
import useLead from '~/features/leadDetails/hooks/useLead';
import { cn } from '~/utils/cnTailwind';

type OfferManagementTableProps = {
    acceptedOffers?: SchemaLoanRequestResolver_GetAcceptedOffers;
    isFetching: boolean;
};

const leadDetailsRoute = getRouteApi('/_app/leads_/details/$leadId');

export const OfferManagementTable: React.FC<OfferManagementTableProps> = ({ acceptedOffers, isFetching }) => {
    const { t } = useTranslation();
    const { leadId } = leadDetailsRoute.useParams();
    const { data: lead } = useLead(leadId);

    if (!acceptedOffers || acceptedOffers.length === 0) {
        return (
            <div className="my-16 flex flex-col items-center justify-center w-full h-full">
                <NoBank className="w-24 h-24 text-grey-dark" />
                <div className="text-center font-bold mt-2 text-grey-dark">{t('OFFER_MANAGEMENT.NO_OFFERS')}</div>
            </div>
        );
    }

    return (
        <DataTable
            className={cn(
                'overflow-x-auto scrollbar-thin scrollbar-thumb-rounded-md scrollbar-thumb-dark',
                'scrollbar-track-grey-stroke scrollbar-track-rounded-md duration-150',
                isFetching && 'opacity-50',
            )}
        >
            <DataTableColumn name={t('OFFER_MANAGEMENT.HEADERS.BANK')} field="bank" />
            <DataTableColumn sortable name={t('OFFER_MANAGEMENT.HEADERS.ACCEPTED_AT')} field="acceptedAt" />
            <DataTableColumn sortable name={t('OFFER_MANAGEMENT.HEADERS.USAGE')} field="financingPurpose" />
            <DataTableColumn
                sortable
                name={t('OFFER_MANAGEMENT.HEADERS.APPLICATION_NUMBER_SHORT')}
                field="providerApplicationNumber"
            />
            <DataTableColumn sortable name={t('OFFER_MANAGEMENT.HEADERS.CREDIT_AMOUNT')} field="creditAmount" />
            <DataTableColumn sortable name={t('OFFER_MANAGEMENT.HEADERS.RUNTIME')} field="runtime" />
            <DataTableColumn sortable name={t('OFFER_MANAGEMENT.HEADERS.RATE')} field="rate" />
            <DataTableColumn sortable name={t('OFFER_MANAGEMENT.HEADERS.INTEREST')} field="interest" />
            <DataTableColumn
                name={t('OFFER_MANAGEMENT.HEADERS.RESIDUAL_DEBT_INSURANCE')}
                field="applicant1ResidualDebtInsurance"
            />
            <DataTableColumn sortable name={t('OFFER_MANAGEMENT.HEADERS.PROVISION')} field="provision" />
            <DataTableColumn
                sortable
                name={t('OFFER_MANAGEMENT.HEADERS.STATUS')}
                field="status"
                customSortFunction={sortStatus as CustomSortFunction}
            />
            <DataTableColumn sortable name={t('OFFER_MANAGEMENT.HEADERS.SENT_AT')} field="sentAt" />
            <DataTableColumn name="" field="actions" />
            {acceptedOffers.map((acceptedOffer) => (
                <OfferManagementTableRow
                    key={acceptedOffer.id}
                    acceptedOffer={acceptedOffer}
                    payload={{
                        acceptedAt: acceptedOffer.acceptedAt,
                        creditAmount: acceptedOffer.creditAmount,
                        financingPurpose: getOfferFinancingPurpose(acceptedOffer, lead),
                        interest: acceptedOffer.interest,
                        providerApplicationNumber: acceptedOffer.providerApplicationNumber,
                        provision: acceptedOffer.provision,
                        rate: acceptedOffer.rate,
                        runtime: acceptedOffer.runtime,
                        sentAt: acceptedOffer.sentAt,
                        status: acceptedOffer.status,
                    }}
                />
            ))}
        </DataTable>
    );
};
